import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus,
  X,
  Info,
  ChevronRight,
  CircleCheck,
  Undo2,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { LoaderSpinner } from '@/shared/ui/page-loader';
import { useActionLoading } from '@/hooks/useActionLoading';
import {
  CHANGE_STYLES,
  CHANGE_LABEL,
  CHANGE_BADGE_STYLES,
  rowToChangeKind,
  changeKindToPersistedState,
  type SlotChangeKind,
} from '../model/availabilityChangeHighlight';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import { DateInput } from '@/shared/ui/date-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/shared/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/collapsible';
import { schedulerApi } from '@/features/scheduler';
import {
  extractDatePart,
  getTodayInTimezone,
  convertDateToUTC,
  convertDateFromUTC,
  formatDateForInput,
  utcIsoToDateAndTimeInTimezone,
} from '@/utils/timezoneUtils';
import { formatHmForDisplay } from '@/utils/timeDisplayFormat';
import {
  validateTimeBlocks,
  checkTimeBlockOverlap,
  dateRangesOverlap,
} from '@/utils/availabilityValidation';
import {
  getBlockingAvailabilityIdsFromCheckDeleteResponse,
  getBlockingSchedulesFromCheckDeleteResponse,
} from '@/shared/lib/moderator-availability-delete';

const BOOKINGS_FONT =
  "font-['Segoe_UI','Segoe_UI_Web','Segoe_UI_Variable',system-ui,-apple-system,sans-serif]";

/** Time row controls — clear hit targets, Bookings-like neutrals */
const TIME_SELECT_TRIGGER =
  'h-9 w-full min-w-[7.5rem] rounded-md border-border bg-background text-sm font-normal shadow-sm focus:ring-2 focus:ring-ring/40 focus:ring-offset-0';
const BOOKINGS_ICON_BTN =
  'h-9 w-9 shrink-0 rounded-md border border-border/70 bg-background text-foreground hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40';

const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export type DaySlot = {
  start: string;
  end: string;
  /**
   * Stable identity tying an edited row back to the baseline (loaded) slot it came
   * from. Preserved across start/end edits so changing the START time still reads as
   * an Update (not a delete + add) — OD-980 AC2. Absent on session-added slots.
   */
  baseKey?: string;
  /** Persisted status of the source record — drives the cross-session highlight (AC5). */
  status?: 'approved' | 'pending_approval';
  /** Persisted change awaiting PM approval on the source record (AC5). */
  pendingAction?: 'add' | 'update' | 'delete' | null;
  /** Source record id (used to carry pending deletions through a re-save). */
  recordId?: string;
};
type WeekSnapshot = Record<number, DaySlot[]>;
type FormSnapshot = {
  rangeStart: string;
  rangeEnd: string;
  week: WeekSnapshot;
};

function emptyWeek(): Record<number, DaySlot[]> {
  return { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
}

/** Deep copy a week's slots (preserving each slot's identity + persisted state). */
function cloneWeek(week: Record<number, DaySlot[]>): Record<number, DaySlot[]> {
  const out = emptyWeek();
  for (let d = 0; d <= 6; d++) {
    out[d] = (week[d] || []).map((slot) => ({ ...slot }));
  }
  return out;
}

function normalizeWeekForCompare(week: Record<number, DaySlot[]>): WeekSnapshot {
  const normalized = emptyWeek();
  for (let d = 0; d <= 6; d++) {
    normalized[d] = [...(week[d] || [])]
      .filter((slot) => slot.start && slot.end)
      .map((slot) => ({ start: slot.start, end: slot.end }))
      .sort((a, b) => `${a.start}-${a.end}`.localeCompare(`${b.start}-${b.end}`));
  }
  return normalized;
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

/** 15-minute increments for availability dropdowns */
const TIME_OPTIONS: string[] = (() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    out.push(`${pad2(h)}:00`);
    out.push(`${pad2(h)}:15`);
    out.push(`${pad2(h)}:30`);
    out.push(`${pad2(h)}:45`);
  }
  return out;
})();
const START_TIME_OPTIONS: string[] = TIME_OPTIONS;
const END_TIME_OPTIONS: string[] = [...TIME_OPTIONS.filter((t) => t !== '00:00'), '00:00'];

const ALLOWED_TIMEZONES = new Set([
  'UTC',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'Europe/Paris',
  'Asia/Shanghai',
  'Asia/Tokyo',
]);

/** Radix Select throws if value is not an exact SelectItem value */
function coerceTimezoneForSelect(tz: string): string {
  return tz && ALLOWED_TIMEZONES.has(tz) ? tz : 'America/Los_Angeles';
}

function padWallTime(t: string): string {
  const p = (t || '00:00').trim().split(':');
  const h = Math.min(23, Math.max(0, Number(p[0]) || 0));
  const m = Math.min(59, Math.max(0, Number(p[1]) || 0));
  return `${pad2(h)}:${pad2(m)}`;
}

/** Map arbitrary HH:mm to nearest 15-minute option in TIME_OPTIONS. */
function snapToQuarterHourOption(t: string): string {
  const normalized = padWallTime(t);
  if (TIME_OPTIONS.includes(normalized)) return normalized;
  const toMinutes = (s: string) => {
    const [h, m] = s.split(':').map(Number);
    return h * 60 + m;
  };
  const target = toMinutes(normalized);
  let best = TIME_OPTIONS[0]!;
  let bestDist = Infinity;
  for (const opt of TIME_OPTIONS) {
    const d = Math.abs(toMinutes(opt) - target);
    if (d < bestDist) {
      bestDist = d;
      best = opt;
    }
  }
  return best;
}

function refDateForWeekday(effectiveFromYmd: string, targetDow: number): string {
  const [y, m, d] = effectiveFromYmd.split('-').map(Number);
  const start = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const startDow = start.getUTCDay();
  const delta = (targetDow - startDow + 7) % 7;
  start.setUTCDate(start.getUTCDate() + delta);
  const yy = start.getUTCFullYear();
  const mm = pad2(start.getUTCMonth() + 1);
  const dd = pad2(start.getUTCDate());
  return `${yy}-${mm}-${dd}`;
}

function addUtcDays(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

function ymdToDow(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).getUTCDay();
}

function composeUtcIso(ymd: string, hhmm: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const [h, min] = String(hhmm || '00:00')
    .split(':')
    .slice(0, 2)
    .map(Number);
  return new Date(Date.UTC(y, m - 1, d, h || 0, min || 0, 0)).toISOString();
}

/** Inverse of utcIsoToDateAndTimeInTimezone: take a wall-clock (YMD + HH:MM) in `tz`
 * and return the UTC Date instant. Used so save can derive UTC day_of_week and
 * start_time from the real UTC instant — matching how the read path anchors records. */
function tzWallTimeToUtcDate(localYmd: string, localHm: string, tz: string): Date {
  const [y, mo, d] = localYmd.split('-').map(Number);
  const [hh, mm] = String(localHm || '00:00').split(':').slice(0, 2).map(Number);
  const target = Date.UTC(y, mo - 1, d, hh || 0, mm || 0, 0);
  let guess = new Date(target);
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
  for (let i = 0; i < 3; i++) {
    const parts = fmt.formatToParts(guess);
    const gv = (t: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((p) => p.type === t)?.value ?? 'NaN');
    let lh = gv('hour');
    if (lh === 24) lh = 0;
    const observed = Date.UTC(gv('year'), gv('month') - 1, gv('day'), lh, gv('minute'), 0);
    if (observed === target) break;
    guess = new Date(guess.getTime() + (target - observed));
  }
  return guess;
}

function formatYmdForDisplay(ymd: string): string {
  const trimmed = (ymd || '').trim();
  if (!trimmed) return '';
  const parts = trimmed.split('-');
  if (parts.length !== 3) return trimmed;
  const [yyyy, mm, dd] = parts;
  if (!yyyy || !mm || !dd) return trimmed;
  return `${dd}/${mm}/${yyyy}`;
}

export type ModeratorPeriod = {
  blockKey: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  records: any[];
  status: 'approved' | 'pending_approval';
  isNew?: boolean;
};

function groupIntoPeriods(records: any[]): ModeratorPeriod[] {
  const map = new Map<string, any[]>();
  for (const r of records) {
    if (!r.effective_from) continue;
    const to = r.effective_to ? extractDatePart(r.effective_to) : null;
    const k = `${extractDatePart(r.effective_from)}_${to || 'ongoing'}`;
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(r);
  }
  const out: ModeratorPeriod[] = [];
  for (const [, recs] of map) {
    if (recs.length === 0) continue;
    const effFrom = extractDatePart(recs[0].effective_from);
    const effTo = recs[0].effective_to ? extractDatePart(recs[0].effective_to) : null;
    // A period awaits approval if any row is pending OR carries a pending change
    // (a pending deletion keeps status='approved' but still needs PM sign-off).
    const st = recs.some(
      (x: any) => x.status === 'pending_approval' || (x.pending_action ?? null) !== null,
    )
      ? 'pending_approval'
      : 'approved';
    out.push({
      blockKey: `${effFrom}_${effTo || 'ongoing'}`,
      effectiveFrom: effFrom,
      effectiveTo: effTo,
      records: recs,
      status: st,
    });
  }
  out.sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  return out;
}

function hmToMinutes(v: string): number {
  const [h, m] = String(v || '00:00')
    .split(':')
    .slice(0, 2)
    .map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

// Convert one stored (UTC) availability record into display-timezone day slots.
// Returns 0, 1, or 2 entries (2 when the interval crosses local midnight).
// Each produced slot carries the source record's identity + persisted change state
// so the highlight can survive a reload.
function recordToWeekSlots(
  r: any,
  safeTz: string,
): Array<{ dow: number; slot: DaySlot }> {
  const recordId = r?.id != null ? String(r.id) : undefined;
  const meta = {
    status: (r?.status ?? 'pending_approval') as 'approved' | 'pending_approval',
    pendingAction: (r?.pending_action ?? null) as 'add' | 'update' | 'delete' | null,
    recordId,
  };
  const withMeta = (slot: DaySlot, seg: number): DaySlot => ({
    ...slot,
    ...meta,
    baseKey: `${recordId ?? 'r'}#${seg}`,
  });
  const dow = Number(r.day_of_week);
  if (Number.isNaN(dow) || dow < 0 || dow > 6) return [];
  const effFrom = extractDatePart(r.effective_from);
  // Build full UTC datetimes from a real reference date for this weekday.
  const anchorUtcYmd =
    effFrom && /^\d{4}-\d{2}-\d{2}$/.test(effFrom)
      ? refDateForWeekday(effFrom, dow)
      : effFrom;
  if (!anchorUtcYmd || !/^\d{4}-\d{2}-\d{2}$/.test(anchorUtcYmd)) return [];

  const startUtcHm = String(r.start_time || '').slice(0, 5);
  const endUtcHm = String(r.end_time || '').slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(startUtcHm) || !/^\d{2}:\d{2}$/.test(endUtcHm)) return [];

  const startUtcIso = composeUtcIso(anchorUtcYmd, startUtcHm);
  const endAnchorUtcYmd =
    hmToMinutes(endUtcHm) <= hmToMinutes(startUtcHm)
      ? addUtcDays(anchorUtcYmd, 1)
      : anchorUtcYmd;
  const endUtcIso = composeUtcIso(endAnchorUtcYmd, endUtcHm);

  // Convert full UTC datetimes to display timezone and derive day/time from converted values.
  const startLocal = utcIsoToDateAndTimeInTimezone(startUtcIso, safeTz);
  const endLocal = utcIsoToDateAndTimeInTimezone(endUtcIso, safeTz);
  if (!startLocal || !endLocal) return [];

  const st = snapToQuarterHourOption(startLocal.time);
  const en = snapToQuarterHourOption(endLocal.time);
  const startDowLocal = ymdToDow(startLocal.date);
  const endDowLocal = ymdToDow(endLocal.date);

  if (startLocal.date === endLocal.date) {
    return [{ dow: startDowLocal, slot: withMeta({ start: st, end: en }, 0) }];
  }

  // Interval crosses local midnight after timezone conversion: split across local day rows.
  const out: Array<{ dow: number; slot: DaySlot }> = [
    { dow: startDowLocal, slot: withMeta({ start: st, end: '00:00' }, 0) },
  ];
  if (en !== '00:00') {
    out.push({ dow: endDowLocal, slot: withMeta({ start: '00:00', end: en }, 1) });
  }
  return out;
}

/**
 * Build the editable weekly grid from records. Rows flagged for pending deletion
 * (pending_action === 'delete') are excluded here — they render separately as
 * struck-through "Deleted" rows (see buildPendingDeletesFromRecords).
 */
function buildWeekFromRecords(recs: any[], tz: string): Record<number, DaySlot[]> {
  const w = emptyWeek();
  const safeTz = coerceTimezoneForSelect(tz);
  for (const r of recs) {
    if (r?.pending_action === 'delete') continue;
    for (const { dow, slot } of recordToWeekSlots(r, safeTz)) {
      w[dow].push(slot);
    }
  }
  for (let d = 0; d <= 6; d++) {
    w[d].sort((a, b) => a.start.localeCompare(b.start));
  }
  return w;
}

/** Build the struck-through "Deleted" rows from records already flagged for removal. */
function buildPendingDeletesFromRecords(recs: any[], tz: string): Record<number, DaySlot[]> {
  const w = emptyWeek();
  const safeTz = coerceTimezoneForSelect(tz);
  for (const r of recs) {
    if (r?.pending_action !== 'delete') continue;
    for (const { dow, slot } of recordToWeekSlots(r, safeTz)) {
      w[dow].push(slot);
    }
  }
  for (let d = 0; d <= 6; d++) {
    w[d].sort((a, b) => a.start.localeCompare(b.start));
  }
  return w;
}

const SYNTHETIC_NEW: ModeratorPeriod = {
  blockKey: '__new__',
  effectiveFrom: '',
  effectiveTo: null,
  records: [],
  status: 'pending_approval',
  isNew: true,
};

/** Distinct record ids referenced by a set of availability records. */
function collectRecordIds(records: any[]): string[] {
  const idSet = new Set<string>();
  for (const r of records) {
    if (r?.id) idSet.add(String(r.id));
  }
  return Array.from(idSet);
}

/** Delete every id outright — used on the PM-approver path where deletion is final. */
async function deleteAllAvailabilityRecords(ids: string[]): Promise<void> {
  for (const id of ids) {
    await schedulerApi.deleteModeratorAvailability(id);
  }
}

/**
 * Moderator path: approved rows need PM sign-off to remove (requested, stay bookable),
 * while never-approved pending rows can just be deleted outright. Returns how many
 * removals were requested (still awaiting approval).
 */
async function deleteOrRequestDeleteAsModerator(records: any[]): Promise<number> {
  let requestedCount = 0;
  for (const r of records) {
    const id = r?.id ? String(r.id) : null;
    if (!id) continue;
    if ((r.status ?? 'pending_approval') === 'approved') {
      await schedulerApi.requestDeleteModeratorAvailability(id);
      requestedCount += 1;
    } else {
      await schedulerApi.deleteModeratorAvailability(id);
    }
  }
  return requestedCount;
}

const CANNOT_DELETE_ACTIVE_SESSIONS_TOAST = {
  title: 'Cannot delete',
  description: 'Availability cannot be deleted while active sessions are present.',
  variant: 'destructive' as const,
};

export interface ModeratorWeeklyAvailabilityCardProps {
  moderatorName: string;
  roleLabel: string;
  initials: string;
  moderatorId: string;
  projectId: string | null;
  projectStartDate?: string | null;
  projectEndDate?: string | null;
  records: any[];
  timezone: string;
  onTimezoneChange: (value: string) => void;
  canEdit: boolean;
  isProjectManager: boolean;
  selectedGroups: Set<string>;
  onToggleGroupSelection: (groupKey: string, checked: boolean) => void;
  onRefresh: () => Promise<void>;
  /** Date ranges of OTHER grids for the same moderator. Used to block save when the new
   * range overlaps another grid (sequential-grid model). */
  otherGridDateRanges?: Array<{ effectiveFrom: string; effectiveTo: string | null }>;
  /** Arbitrary JSX rendered as a banner at the top of the card body (above the date-range
   * checkbox). Used to host the schedule-tabs strip when several grids share one moderator. */
  topBanner?: React.ReactNode;
  /** Hide the moderator header row (avatar/name/timezone) when several cards stack
   * under one moderator and the header is already rendered once. */
  hideHeader?: boolean;
  /** Shown beside Save when the card is in "new schedule" mode. Lets the parent
   * close the new tab without persisting. */
  onCancelNew?: () => void;
  /** Called after a successful save with the persisted period's effective dates.
   * Used so the parent can switch the active tab from "__new__" to the saved period. */
  onSaved?: (effectiveFrom: string | null, effectiveTo: string | null) => void;
}

export function ModeratorWeeklyAvailabilityCard({
  moderatorName,
  roleLabel,
  initials,
  moderatorId,
  projectId,
  projectStartDate,
  projectEndDate,
  records,
  timezone,
  onTimezoneChange,
  canEdit,
  isProjectManager,
  selectedGroups,
  onToggleGroupSelection,
  onRefresh,
  otherGridDateRanges,
  topBanner,
  hideHeader,
  onCancelNew,
  onSaved,
}: Readonly<ModeratorWeeklyAvailabilityCardProps>) {
  const { toast } = useToast();

  const periods = useMemo(() => groupIntoPeriods(records), [records]);

  const displayTimezone = useMemo(() => coerceTimezoneForSelect(timezone), [timezone]);

  const [periodsVersion, setPeriodsVersion] = useState(0);

  /** Single surface: edit the most recently starting period (no schedule picker). */
  const selectedPeriod = useMemo(() => {
    if (periods.length === 0) return SYNTHETIC_NEW;
    return periods[periods.length - 1]!;
  }, [periods]);

  /** Moderator availability view should show all rows (approved + pending). */
  const activePeriodRecords = useMemo(() => {
    return selectedPeriod.records;
  }, [selectedPeriod]);

  /** Content-only key so hydration does not re-run when `records` is a new array reference with the same rows. */
  const recordsSyncFingerprint = useMemo(
    () =>
      [...records]
        .map((r: any) =>
          [
            r.id,
            extractDatePart(r.effective_from),
            extractDatePart(r.effective_to),
            String(r.day_of_week ?? ''),
            String(r.start_time ?? ''),
            String(r.end_time ?? ''),
            String(r.status ?? ''),
            String(r.pending_action ?? ''),
          ].join(':'),
        )
        .sort((a, b) => a.localeCompare(b))
        .join('|'),
    [records],
  );

  // Compute the initial form state synchronously from props so the very first paint
  // already reflects the active period. Without this, the card briefly shows the
  // default (empty) state on mount before the populating effect runs — causing a
  // visible flicker when switching tabs (each tab mounts a fresh card).
  const initialFormState = (() => {
    const safeTz = coerceTimezoneForSelect(timezone);
    if (!records || records.length === 0) {
      return { week: emptyWeek(), pendingDeletes: emptyWeek(), rangeStart: '', rangeEnd: '' };
    }
    const firstRec: any = records[0];
    const week = buildWeekFromRecords(records, safeTz);
    const pendingDeletes = buildPendingDeletesFromRecords(records, safeTz);
    const start = formatDateForInput(extractDatePart(firstRec.effective_from));
    const rawEnd = firstRec.effective_to
      ? formatDateForInput(extractDatePart(firstRec.effective_to))
      : '';
    const end = rawEnd && rawEnd < start ? start : rawEnd;
    return { week, pendingDeletes, rangeStart: start, rangeEnd: end };
  });

  const [rangeStart, setRangeStart] = useState(() => initialFormState().rangeStart);
  const [rangeEnd, setRangeEnd] = useState(() => initialFormState().rangeEnd);
  const [dateErrors, setDateErrors] = useState<{ start: boolean; end: boolean }>({
    start: false,
    end: false,
  });
  const [week, setWeek] = useState<Record<number, DaySlot[]>>(
    () => initialFormState().week,
  );
  // Baseline = the slots as last loaded (carrying each slot's identity + persisted
  // status/pending_action). The session diff is measured against this, and it is
  // also the source for the cross-session highlight.
  const [baselineWeek, setBaselineWeek] = useState<Record<number, DaySlot[]>>(
    () => cloneWeek(initialFormState().week),
  );
  // Rows already flagged for deletion (pending_action === 'delete'): shown struck
  // through and carried through re-saves until a PM approves/rejects the removal.
  const [pendingDeletes, setPendingDeletes] = useState<Record<number, DaySlot[]>>(
    () => initialFormState().pendingDeletes,
  );
  const [saving, setSaving] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState<FormSnapshot | null>(() => {
    const s = initialFormState();
    return {
      rangeStart: s.rangeStart,
      rangeEnd: s.rangeEnd,
      week: normalizeWeekForCompare(s.week),
    };
  });
  /** Weekly grid + date range + save actions (header/timezone stay visible). */
  const [scheduleOpen, setScheduleOpen] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const minSelectableDate = useMemo(
    () => getTodayInTimezone(displayTimezone),
    [displayTimezone],
  );

  const overlapWarning = useMemo(() => {
    for (let d = 0; d <= 6; d++) {
      const slots = week[d];
      if (slots.length >= 2 && checkTimeBlockOverlap(slots)) {
        return 'Times overlap with another set of times.';
      }
    }
    return null;
  }, [week]);

  // YMDs (in displayTimezone) of active sessions tied to this period's availability rows.
  // Used to block date-range changes that would drop a scheduled session out of the range.
  // Raw (UTC) blocking-session start times from check-delete — kept untransformed so a
  // timezone-selector change can be re-rendered in the new zone via the memo below without
  // re-hitting the network (the check-delete request itself doesn't depend on timezone at all).
  const [blockingSessionStartTimes, setBlockingSessionStartTimes] = useState<string[]>([]);

  const periodRecordIdsKey = useMemo(() => {
    const ids = (selectedPeriod.records || [])
      .map((r: any) => r?.id)
      .filter((id: any): id is string => !!id)
      .map(String)
      .sort((a, b) => a.localeCompare(b));
    return ids.join(',');
  }, [selectedPeriod.records]);

  // Guards against a duplicate check-delete request for the same id set —
  // e.g. if the parent's own records fetch resolves in two steps (a
  // transient partial set, then the final one) and this effect re-runs
  // before the first request has settled. Keyed by periodRecordIdsKey
  // itself (not a boolean) so a genuinely different id set still fires.
  const inFlightCheckDeleteKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedPeriod.isNew || !periodRecordIdsKey) {
      setBlockingSessionStartTimes([]);
      return;
    }
    const ids = periodRecordIdsKey.split(',').filter(Boolean);
    if (ids.length === 0) {
      setBlockingSessionStartTimes([]);
      return;
    }
    if (inFlightCheckDeleteKeyRef.current === periodRecordIdsKey) return;
    inFlightCheckDeleteKeyRef.current = periodRecordIdsKey;
    let cancelled = false;
    (async () => {
      try {
        const res = await schedulerApi.checkModeratorAvailabilityDelete(ids);
        const blocking = getBlockingSchedulesFromCheckDeleteResponse(res) as Array<{
          start_time?: string;
        }>;
        const startTimes = blocking
          .map((s) => s?.start_time)
          .filter((t): t is string => !!t);
        if (!cancelled) setBlockingSessionStartTimes(startTimes);
      } catch {
        if (!cancelled) setBlockingSessionStartTimes([]);
      } finally {
        if (inFlightCheckDeleteKeyRef.current === periodRecordIdsKey) {
          inFlightCheckDeleteKeyRef.current = null;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [periodRecordIdsKey, selectedPeriod.isNew]);

  const sessionYmdsInPeriod = useMemo(() => {
    const ymds = blockingSessionStartTimes
      .map((t) => utcIsoToDateAndTimeInTimezone(t, displayTimezone)?.date)
      .filter((d): d is string => !!d);
    return Array.from(new Set(ymds)).sort((a, b) => a.localeCompare(b));
  }, [blockingSessionStartTimes, displayTimezone]);

  // Inline error when the proposed date range would drop an existing scheduled session.
  const sessionDateConflict = useMemo<string | null>(() => {
    if (sessionYmdsInPeriod.length === 0) return null;
    const earliest = sessionYmdsInPeriod[0];
    const latest = sessionYmdsInPeriod[sessionYmdsInPeriod.length - 1];
    if (rangeStart && earliest && rangeStart > earliest) {
      return `Start date can't be after ${formatYmdForDisplay(earliest)} — an active session exists on that day.`;
    }
    if (rangeEnd && latest && rangeEnd < latest) {
      return `End date can't be before ${formatYmdForDisplay(latest)} — an active session exists on that day.`;
    }
    return null;
  }, [sessionYmdsInPeriod, rangeStart, rangeEnd]);

  const daysWithHours = useMemo(
    () => WEEKDAY_LABELS.reduce((n, _, i) => n + (week[i]?.length ? 1 : 0), 0),
    [week],
  );

  const hasChanges = useMemo(() => {
    if (!initialSnapshot) return false;
    const normalizedCurrentWeek = normalizeWeekForCompare(week);
    const hasAnySlots = Object.values(normalizedCurrentWeek).some((slots) => slots.length > 0);
    if (!hasAnySlots) return false;

    if (selectedPeriod.isNew) {
      return JSON.stringify(normalizedCurrentWeek) !== JSON.stringify(initialSnapshot.week);
    }

    const currentSnapshot: FormSnapshot = {
      rangeStart,
      rangeEnd,
      week: normalizedCurrentWeek,
    };
    return JSON.stringify(currentSnapshot) !== JSON.stringify(initialSnapshot);
  }, [initialSnapshot, selectedPeriod.isNew, rangeStart, rangeEnd, week]);

  const resetFormToInitialSnapshot = () => {
    if (!initialSnapshot) return;
    // Restore from the identity-carrying baseline (not the normalized snapshot) so
    // slot identity + persisted highlight state survive the reset.
    setWeek(cloneWeek(baselineWeek));
    setPendingDeletes(buildPendingDeletesFromRecords(activePeriodRecords, displayTimezone));
    setRangeStart(initialSnapshot.rangeStart);
    setRangeEnd(initialSnapshot.rangeEnd);
    setDateErrors({ start: false, end: false });
  };

  useEffect(() => {
    if (selectedPeriod.isNew) {
      const nextWeek = emptyWeek();
      const nextRangeStart = '';
      const nextRangeEnd = '';
      setWeek(nextWeek);
      setBaselineWeek(cloneWeek(nextWeek));
      setPendingDeletes(emptyWeek());
      setRangeStart(nextRangeStart);
      setRangeEnd(nextRangeEnd);
      setDateErrors({ start: false, end: false });
      setInitialSnapshot({
        rangeStart: nextRangeStart,
        rangeEnd: nextRangeEnd,
        week: normalizeWeekForCompare(nextWeek),
      });
      return;
    }
    const recs = activePeriodRecords;
    const nextWeek = buildWeekFromRecords(recs, displayTimezone);
    const nextPendingDeletes = buildPendingDeletesFromRecords(recs, displayTimezone);
    const existingStart = formatDateForInput(extractDatePart(selectedPeriod.effectiveFrom));
    // Preserve stored dates for existing records; do not force-display "today".
    const clampedStart = existingStart;
    const existingEnd = selectedPeriod.effectiveTo
      ? formatDateForInput(extractDatePart(selectedPeriod.effectiveTo))
      : '';
    const clampedEnd =
      existingEnd && existingEnd < clampedStart ? clampedStart : existingEnd;
    setWeek(nextWeek);
    setBaselineWeek(cloneWeek(nextWeek));
    setPendingDeletes(nextPendingDeletes);
    setRangeStart(clampedStart);
    setRangeEnd(clampedEnd);
    setDateErrors({ start: false, end: false });
    setInitialSnapshot({
      rangeStart: clampedStart,
      rangeEnd: clampedEnd,
      week: normalizeWeekForCompare(nextWeek),
    });
    // Keyed off `recordsSyncFingerprint` (not `records` / `selectedPeriod` identity) so parent re-renders do not reset dates/slots.
  }, [recordsSyncFingerprint, displayTimezone, periodsVersion, minSelectableDate, activePeriodRecords]);

  const groupKey = `${moderatorId}:${selectedPeriod.blockKey}`;
  const approveControlId = `ap-${groupKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const canSelectApprove =
    isProjectManager && selectedPeriod.status === 'pending_approval' && !selectedPeriod.isNew;
  const isSelected = selectedGroups.has(groupKey);
  const isPmPendingApprovalPeriod =
    isProjectManager && !selectedPeriod.isNew && selectedPeriod.status === 'pending_approval';
  const showPendingApprovalBadge = selectedPeriod.records.some(
    (r: any) =>
      (r.status ?? 'pending_approval') === 'pending_approval' ||
      (r.pending_action ?? null) !== null,
  );
  const showApprovedBadge =
    selectedPeriod.records.length > 0 &&
    !showPendingApprovalBadge &&
    selectedPeriod.records.every(
      (r: any) => (r.status ?? 'pending_approval') === 'approved',
    );
  const pmPrimaryLabel = hasChanges ? 'Save and Approve' : 'Approve';

  // Per-action busy state so every button shows a loader while its action runs.
  const { run: runAction, isBusy } = useActionLoading();

  /**
   * Ghost (struck-through "Deleted") rows shown for a day. Two sources, both kept
   * highlighted until a PM approves:
   *  - persisted: rows already flagged pending_action === 'delete'
   *  - session:   baseline slots the moderator removed this session (restorable)
   * Keyed by baseKey so a removed row is matched to its baseline by identity, not
   * by start time (AC2) — changing a start time no longer looks like delete + add.
   */
  const deletedGhostsByDay = useMemo(() => {
    const out: Record<number, DaySlot[]> = emptyWeek();
    for (let d = 0; d <= 6; d++) {
      const presentKeys = new Set(
        (week[d] ?? []).map((s) => s.baseKey).filter(Boolean),
      );
      const sessionDeleted = (baselineWeek[d] ?? []).filter(
        (b) => b.baseKey && !presentKeys.has(b.baseKey),
      );
      out[d] = [...(pendingDeletes[d] ?? []), ...sessionDeleted].sort((a, z) =>
        a.start.localeCompare(z.start),
      );
    }
    return out;
  }, [week, baselineWeek, pendingDeletes]);

  /**
   * Classify a still-present (editable) slot for highlighting.
   * Identity-based (baseKey), so editing either the start OR the end of an existing
   * slot reads as Updated — never delete + add (OD-980 AC2). A slot with no baseline
   * match was added this session. When a slot matches its baseline unchanged, the
   * persisted pending_action/status decides the highlight so it survives reload (AC5).
   */
  const editableSlotKind = (dow: number, slot: DaySlot): SlotChangeKind => {
    if (!slot.start || !slot.end) return 'unchanged';
    if (!slot.baseKey) return 'added';
    const base = (baselineWeek[dow] ?? []).find((b) => b.baseKey === slot.baseKey);
    if (!base) return 'added';
    if (base.start !== slot.start || base.end !== slot.end) return 'updated';
    return rowToChangeKind({ status: slot.status, pending_action: slot.pendingAction });
  };

  const addSlot = (dow: number) => {
    setWeek((prev) => ({
      ...prev,
      [dow]: [...prev[dow], { start: '08:00', end: '17:00' }],
    }));
  };

  /** Re-add a deleted (ghost) slot back into the editable grid, keeping its identity. */
  const restoreSlot = (dow: number, slot: DaySlot) => {
    setPendingDeletes((prev) => ({
      ...prev,
      [dow]: (prev[dow] ?? []).filter((s) => s.baseKey !== slot.baseKey),
    }));
    setWeek((prev) => ({
      ...prev,
      [dow]: [...prev[dow], { ...slot, pendingAction: null }],
    }));
  };

  const removeSlot = (dow: number, idx: number) => {
    setWeek((prev) => ({
      ...prev,
      [dow]: prev[dow].filter((_, i) => i !== idx),
    }));
  };

  const updateSlot = (dow: number, idx: number, field: 'start' | 'end', value: string) => {
    setWeek((prev) => {
      const next = { ...prev, [dow]: [...prev[dow]] };
      next[dow][idx] = { ...next[dow][idx], [field]: value };
      return next;
    });
  };

  // Approve each pending row; collect how many succeeded and which days were blocked.
  const approvePendingAvailabilityRows = async (
    pendingRows: any[],
  ): Promise<{ approvedCount: number; blockedDayLabels: Set<string> }> => {
    const blockedDayLabels = new Set<string>();
    let approvedCount = 0;
    for (const row of pendingRows) {
      const id = row?.id;
      if (!id) continue;
      try {
        await schedulerApi.approveModeratorAvailability(id, displayTimezone);
        approvedCount += 1;
      } catch (err: any) {
        const labels = err?.response?.data?.blocked_day_labels;
        if (Array.isArray(labels) && labels.length > 0) {
          labels.forEach((label: string) => blockedDayLabels.add(label));
        } else {
          const dow = Number(row?.day_of_week);
          if (Number.isInteger(dow) && dow >= 0 && dow <= 6) {
            blockedDayLabels.add(WEEKDAY_LABELS[dow]);
          }
        }
      }
    }
    return { approvedCount, blockedDayLabels };
  };

  // Toast the outcome of an approval pass (full / partial / none).
  const showApprovalResultToast = (
    approvedCount: number,
    blockedDayLabels: Set<string>,
  ) => {
    const dayList = Array.from(blockedDayLabels).join(', ');
    if (approvedCount > 0 && blockedDayLabels.size === 0) {
      toast({ title: 'Success', description: 'Approved successfully.' });
      return;
    }
    if (approvedCount > 0) {
      toast({
        title: 'Partial approval',
        description: `Approved non-conflicting availability. Could not approve ${dayList} while active sessions are present.`,
        variant: 'destructive',
      });
      return;
    }
    toast({
      title: 'Cannot approve',
      description:
        dayList.length > 0
          ? `Availability cannot be approved for ${dayList} while active sessions are present.`
          : 'Failed to approve availability.',
      variant: 'destructive',
    });
  };

  const handleApprovePeriod = async () => {
    if (!isProjectManager || selectedPeriod.records.length === 0) return;
    try {
      // Approve pending rows AND rows carrying a pending change — a pending deletion
      // keeps status='approved' but still needs the PM to finalize it.
      const pendingRows = selectedPeriod.records.filter(
        (r: any) =>
          (r.status ?? 'pending_approval') === 'pending_approval' ||
          (r.pending_action ?? null) !== null,
      );
      const pendingIds = pendingRows.map((r: any) => r.id).filter(Boolean);

      if (pendingIds.length === 0) {
        toast({ title: 'Info', description: 'No pending availability to approve.' });
        return;
      }

      const { approvedCount, blockedDayLabels } =
        await approvePendingAvailabilityRows(pendingRows);
      showApprovalResultToast(approvedCount, blockedDayLabels);

      await onRefresh();
    } catch (e: any) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error || 'Failed to approve.',
        variant: 'destructive',
      });
    }
  };

  // Check whether active sessions block deleting these availability records;
  // toasts and returns false when a conflict blocks the operation.
  const canDeleteWithoutSessionConflict = async (ids: string[]): Promise<boolean> => {
    const res = await schedulerApi.checkModeratorAvailabilityDelete(ids);
    const blocking = getBlockingSchedulesFromCheckDeleteResponse(res);
    if (blocking.length > 0) {
      toast(CANNOT_DELETE_ACTIVE_SESSIONS_TOAST);
      return false;
    }
    return true;
  };

  // Clear the grid back to empty and confirm the removal.
  const resetWeekToEmptyAndToastCleared = () => {
    setWeek(emptyWeek());
    setRangeStart(minSelectableDate);
    setRangeEnd('');
    setDateErrors({ start: false, end: false });
    toast({ title: 'Success', description: 'All working hours removed for this moderator.' });
  };

  // 409 (active-session) vs any other delete/validate failure.
  const handleDeleteApiError = (e: any, fallbackDescription: string) => {
    if (e?.response?.status === 409) {
      toast(CANNOT_DELETE_ACTIVE_SESSIONS_TOAST);
      return;
    }
    toast({ title: 'Error', description: fallbackDescription, variant: 'destructive' });
  };

  const handleDeletePeriod = async () => {
    if (!canEdit || !projectId || selectedPeriod.isNew) return;
    const ids = collectRecordIds(records);
    if (ids.length === 0) return;
    try {
      const canDelete = await canDeleteWithoutSessionConflict(ids);
      if (!canDelete) return;

      if (isProjectManager) {
        // PM is the approver — deletion takes effect immediately.
        await deleteAllAvailabilityRecords(ids);
        resetWeekToEmptyAndToastCleared();
      } else {
        // Moderator — approved slots need PM approval, so request deletion (they
        // stay bookable until approved). Never-approved pending adds are removed.
        const requestedCount = await deleteOrRequestDeleteAsModerator(records);
        if (requestedCount > 0) {
          // Approved rows are now flagged pending_action='delete' (still bookable)
          // — leave them visible; the PM will approve the removal.
          toast({
            title: 'Deletion requested',
            description: 'Your removal is awaiting PM approval.',
          });
        } else {
          resetWeekToEmptyAndToastCleared();
        }
      }
      await onRefresh();
      setPeriodsVersion((v) => v + 1);
    } catch (e: any) {
      handleDeleteApiError(e, 'Failed to delete schedule.');
    }
  };

  const handleDeleteScheduleClick = async () => {
    if (!canEdit || !projectId || selectedPeriod.isNew) return;
    const ids = collectRecordIds(records);
    if (ids.length === 0) return;
    try {
      const canDelete = await canDeleteWithoutSessionConflict(ids);
      if (canDelete) setShowDeleteConfirm(true);
    } catch (e: any) {
      handleDeleteApiError(e, 'Failed to validate delete action.');
    }
  };

  // First time-block validation error across the week, or null when valid.
  const getWeekTimeBlockError = (): {title: string; description: string} | null => {
    for (let d = 0; d <= 6; d++) {
      const slots = week[d];
      const err = validateTimeBlocks(slots);
      if (err) return {title: 'Validation', description: err};
      if (checkTimeBlockOverlap(slots)) {
        return {
          title: 'Validation',
          description: `Overlapping times on ${WEEKDAY_LABELS[d]}.`,
        };
      }
    }
    return null;
  };

  // Message describing an overlap with another schedule's date range, or null.
  const getDateRangeConflictMessage = (
    proposedFrom: string,
    proposedTo: string,
  ): string | null => {
    if (!otherGridDateRanges || otherGridDateRanges.length === 0) return null;
    const conflict = otherGridDateRanges.find((other) =>
      dateRangesOverlap(proposedFrom, proposedTo, other.effectiveFrom, other.effectiveTo)
    );
    if (!conflict) return null;
    const conflictLabel = conflict.effectiveTo
      ? `${formatYmdForDisplay(conflict.effectiveFrom)} – ${formatYmdForDisplay(conflict.effectiveTo)}`
      : `from ${formatYmdForDisplay(conflict.effectiveFrom)} onward`;
    return `This date range overlaps another schedule (${conflictLabel}). Pick non-overlapping dates.`;
  };

  // Anchor at a local calendar day whose weekday matches the row's local DOW, then
  // build one UTC availability row. refDateForWeekday returns a YMD whose
  // UTC-weekday == localDow; we treat it as a local YMD (safe because a calendar
  // day's weekday is the same property regardless of which timezone reads it).
  const buildUtcAvailabilityRow = ({
    anchorFrom,
    utcStartDate,
    utcEndDate,
    localDow,
    start,
    end,
    status,
    pendingAction,
  }: {
    anchorFrom: string;
    utcStartDate: string | null;
    utcEndDate: string | null;
    localDow: number;
    start: string;
    end: string;
    status: string;
    pendingAction: 'add' | 'update' | 'delete' | null;
  }) => {
    const localAnchorYmd = refDateForWeekday(anchorFrom, localDow);
    const startUtcDate = tzWallTimeToUtcDate(localAnchorYmd, start, displayTimezone);
    const endLocalAnchorYmd =
      hmToMinutes(end) <= hmToMinutes(start) ? addUtcDays(localAnchorYmd, 1) : localAnchorYmd;
    const endUtcDate = tzWallTimeToUtcDate(endLocalAnchorYmd, end, displayTimezone);
    return {
      moderator_id: moderatorId,
      day_of_week: startUtcDate.getUTCDay(),
      start_time: `${pad2(startUtcDate.getUTCHours())}:${pad2(startUtcDate.getUTCMinutes())}`,
      end_time: `${pad2(endUtcDate.getUTCHours())}:${pad2(endUtcDate.getUTCMinutes())}`,
      slot_duration_minutes: 30,
      timezone: 'UTC',
      effective_from: utcStartDate,
      effective_to: utcEndDate,
      project_id: projectId,
      status,
      pending_action: pendingAction,
    };
  };

  // Present slots for one day — tag each from its diff kind, falling back to its
  // carried (persisted) state.
  const buildPresentSlotRows = (
    localDow: number,
    anchorFrom: string,
    utcStartDate: string | null,
    utcEndDate: string | null,
  ): any[] => {
    const rows: any[] = [];
    for (const slot of week[localDow]) {
      if (!slot.start || !slot.end) continue;
      const kind = editableSlotKind(localDow, slot);
      const persisted = changeKindToPersistedState(kind, isProjectManager);
      const status = persisted ? persisted.status : slot.status ?? 'approved';
      const pendingAction = persisted ? persisted.pending_action : slot.pendingAction ?? null;
      rows.push(
        buildUtcAvailabilityRow({
          anchorFrom,
          utcStartDate,
          utcEndDate,
          localDow,
          start: slot.start,
          end: slot.end,
          status,
          pendingAction,
        }),
      );
    }
    return rows;
  };

  // Moderator-only: pending deletions plus newly-removed approved slots for one day —
  // both carried forward flagged for deletion (still bookable) until the PM approves.
  // Never-approved pending rows just vanish (no flag needed).
  const computeCarriedDeleteSlots = (localDow: number): DaySlot[] => [
    ...(pendingDeletes[localDow] ?? []),
    ...(baselineWeek[localDow] ?? []).filter(
      (b) =>
        b.baseKey &&
        (b.status ?? 'approved') === 'approved' &&
        !week[localDow].some((s) => s.baseKey === b.baseKey),
    ),
  ];

  const buildCarriedDeleteRows = (
    localDow: number,
    carriedDeletes: DaySlot[],
    anchorFrom: string,
    utcStartDate: string | null,
    utcEndDate: string | null,
  ): any[] => {
    const rows: any[] = [];
    for (const del of carriedDeletes) {
      if (!del.start || !del.end) continue;
      rows.push(
        buildUtcAvailabilityRow({
          anchorFrom,
          utcStartDate,
          utcEndDate,
          localDow,
          start: del.start,
          end: del.end,
          status: 'approved',
          pendingAction: 'delete',
        }),
      );
    }
    return rows;
  };

  // Convert each day's wall-time slots into UTC availability rows for the API.
  // Each row carries the (status, pending_action) it should persist as so the
  // Added/Updated/Deleted highlight survives reload until a PM approves :
  //  - present slots are tagged from their diff kind (add/update) or their carried
  //    persisted state (unchanged)
  //  - removed approved slots are re-sent as pending_action='delete' (moderator only),
  //    keeping the row bookable until the PM approves the removal
  const buildWeeklyAvailabilityRows = (
    utcStartDate: string | null,
    utcEndDate: string | null,
    anchorFrom: string,
  ): any[] => {
    const newRows: any[] = [];
    for (let localDow = 0; localDow <= 6; localDow++) {
      newRows.push(...buildPresentSlotRows(localDow, anchorFrom, utcStartDate, utcEndDate));
      if (isProjectManager) continue;
      const carriedDeletes = computeCarriedDeleteSlots(localDow);
      newRows.push(
        ...buildCarriedDeleteRows(localDow, carriedDeletes, anchorFrom, utcStartDate, utcEndDate),
      );
    }
    return newRows;
  };

  // Handle a 409 (active-session) save conflict; returns true if it was one.
  const handleSaveConflictError = (err: any): boolean => {
    if (err?.response?.status !== 409) return false;
    const blockedDayLabels = Array.isArray(err?.response?.data?.blocked_day_labels)
      ? err.response.data.blocked_day_labels
      : [];
    const blockedByDateRangeDayLabels = Array.isArray(
      err?.response?.data?.blocked_by_date_range_day_labels,
    )
      ? err.response.data.blocked_by_date_range_day_labels
      : [];
    const blockedBySlotUpdateDayLabels = Array.isArray(
      err?.response?.data?.blocked_by_slot_update_day_labels,
    )
      ? err.response.data.blocked_by_slot_update_day_labels
      : [];
    const isDateRangeOnlyConflict =
      blockedByDateRangeDayLabels.length > 0 &&
      blockedBySlotUpdateDayLabels.length === 0;
    let description: string;
    if (isDateRangeOnlyConflict) {
      description =
        'Date range cannot be updated while active sessions are present. Keep the range covering existing sessions, or reschedule/cancel those sessions first.';
    } else if (blockedDayLabels.length > 0) {
      description = `Availability slots cannot be updated for ${blockedDayLabels.join(', ')} while active sessions are present.`;
    } else {
      description = 'Availability cannot be updated while active sessions are present.';
    }
    toast({title: 'Cannot update', description, variant: 'destructive'});
    resetFormToInitialSnapshot();
    return true;
  };

  // Persist the period to the API, then toast success and refresh.
  const persistAvailabilityPeriod = async (
    utcStartDate: string | null,
    utcEndDate: string | null,
    newRows: any[],
  ) => {
    const previousEffectiveFrom = selectedPeriod.isNew
      ? utcStartDate
      : extractDatePart(selectedPeriod.effectiveFrom);
    const previousEffectiveTo = selectedPeriod.isNew
      ? utcEndDate
      : selectedPeriod.effectiveTo
        ? extractDatePart(selectedPeriod.effectiveTo)
        : null;
    await schedulerApi.updateModeratorAvailabilityPeriod(moderatorId, {
      project_id: projectId,
      previous_effective_from: previousEffectiveFrom,
      previous_effective_to: previousEffectiveTo,
      effective_from: utcStartDate,
      effective_to: utcEndDate,
      slot_duration_minutes: 30,
      timezone: 'UTC',
      // Lets the backend label/group history entries by local weekday, not the
      // raw UTC day_of_week bucket (OD-892 — an evening slot can cross UTC
      // midnight and land in the next UTC day's bucket).
      display_timezone: displayTimezone,
      status: isProjectManager ? 'approved' : 'pending_approval',
      weekly_slots: newRows.map((row) => ({
        day_of_week: row.day_of_week,
        start_time: row.start_time,
        end_time: row.end_time,
        slot_duration_minutes: row.slot_duration_minutes,
        timezone: row.timezone,
        status: row.status,
        pending_action: row.pending_action ?? null,
      })),
    });
    toast({
      title: 'Success',
      description: isProjectManager
        ? 'Saved and approved successfully.'
        : 'Saved and requested for approval.',
    });
    await onRefresh();
    setPeriodsVersion((v) => v + 1);
    onSaved?.(utcStartDate, utcEndDate);
  };

  const handleSave = async () => {
    if (!canEdit || !projectId) return;

    const todayTz = getTodayInTimezone(displayTimezone);
    const wallStart = rangeStart.trim();
    const wallEnd = rangeEnd.trim();
    const missingStart = !wallStart;
    const missingEnd = !wallEnd;
    if (missingStart || missingEnd) {
      setDateErrors({ start: missingStart, end: missingEnd });
      toast({
        title: 'Validation',
        description: 'Start and end dates are required.',
        variant: 'destructive',
      });
      return;
    }
    if (wallEnd < wallStart) {
      toast({
        title: 'Invalid dates',
        description: 'End date must be on or after start date.',
        variant: 'destructive',
      });
      return;
    }

    const conflictMessage = getDateRangeConflictMessage(wallStart, wallEnd);
    if (conflictMessage) {
      toast({
        title: 'Overlapping schedule',
        description: conflictMessage,
        variant: 'destructive',
      });
      return;
    }

    const weekError = getWeekTimeBlockError();
    if (weekError) {
      toast({...weekError, variant: 'destructive'});
      return;
    }

    // Calendar range: store YYYY-MM-DD literally from the picker (or date part of ISO) so the
    // chosen day is not shifted when the browser TZ ≠ working timezone.
    const toStoredRangeDate = (s: string) => {
      const t = s.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
      const part = extractDatePart(t);
      if (/^\d{4}-\d{2}-\d{2}$/.test(part)) return part;
      return convertDateToUTC(t, displayTimezone);
    };
    const utcStartDate = wallStart ? toStoredRangeDate(wallStart) : null;
    const utcEndDate = wallEnd ? toStoredRangeDate(wallEnd) : null;

    const anchorFrom = utcStartDate || todayTz;
    const newRows = buildWeeklyAvailabilityRows(utcStartDate, utcEndDate, anchorFrom);

    if (newRows.length === 0) {
      toast({
        title: 'Nothing to save',
        description: 'Add at least one time interval on a day, or delete the schedule instead.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      await persistAvailabilityPeriod(utcStartDate, utcEndDate, newRows);
    } catch (e) {
      console.error(e);
      const err = e as any;
      if (handleSaveConflictError(err)) return;
      toast({
        title: 'Error',
        description: err?.response?.data?.error || 'Failed to save working hours.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const renderModeratorCardHeader = () => (
          <div className="flex flex-col gap-3 border-b border-border bg-muted/40 px-4 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
            <div className="flex min-w-0 flex-1 items-start gap-2 sm:items-center sm:gap-3">
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mt-0.5 h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground sm:mt-0"
                  aria-expanded={scheduleOpen}
                  aria-label={scheduleOpen ? 'Collapse weekly schedule' : 'Expand weekly schedule'}
                >
                  <ChevronRight
                    className={cn(
                      'h-5 w-5 transition-transform duration-200',
                      scheduleOpen && 'rotate-90',
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
              <Avatar className="mt-0.5 h-10 w-10 shrink-0 ring-2 ring-background sm:mt-0">
                <AvatarFallback className="bg-muted text-sm font-semibold text-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-base font-semibold tracking-tight text-foreground">
                    {moderatorName}
                  </p>
                  <Badge variant="secondary" className="shrink-0 text-xs font-normal">
                    {roleLabel}
                  </Badge>
                  {showPendingApprovalBadge && (
                    <Badge
                      variant="outline"
                      className="shrink-0 border-amber-200 bg-amber-50 text-xs font-medium text-amber-900"
                    >
                      Pending approval
                    </Badge>
                  )}
                  {showApprovedBadge && (
                    <Badge
                      variant="outline"
                      className="shrink-0 border-emerald-200 bg-emerald-50 text-xs font-medium text-emerald-900"
                    >
                      Approved
                    </Badge>
                  )}
                </div>
                {!scheduleOpen && (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {daysWithHours} day{daysWithHours !== 1 ? 's' : ''} with hours
                    {overlapWarning ? ' · Expand to fix overlapping times' : ' · Expand to edit dates & weekly hours'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex w-full min-w-0 items-center gap-1.5 sm:w-auto lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl">
              <Select value={displayTimezone} onValueChange={onTimezoneChange}>
                <SelectTrigger className="h-10 w-full min-w-0 rounded-md border-border bg-background shadow-sm sm:min-w-[280px] lg:min-w-[400px] xl:min-w-[480px] 2xl:min-w-[560px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">(UTC+0:00) GMT - Europe/London</SelectItem>
                  <SelectItem value="America/Los_Angeles">(UTC-8:00) PST - America/Los_Angeles</SelectItem>
                  <SelectItem value="America/Denver">(UTC-7:00) MST - America/Denver</SelectItem>
                  <SelectItem value="America/Chicago">(UTC-6:00) CST - America/Chicago</SelectItem>
                  <SelectItem value="America/New_York">(UTC-5:00) EST - America/New_York</SelectItem>
                  <SelectItem value="Europe/Paris">(UTC+1:00) CET - Europe/Paris</SelectItem>
                  <SelectItem value="Asia/Shanghai">(UTC+8:00) CST - Asia/Shanghai</SelectItem>
                  <SelectItem value="Asia/Tokyo">(UTC+9:00) JST - Asia/Tokyo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
  );

  const renderModeratorCardActions = () => (
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border bg-muted/30 px-4 py-4 sm:px-6">
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            {!selectedPeriod.isNew && records.length > 0 && (
              <Button
                type="button"
                variant="outline"
                className="h-10 border-destructive/40 text-destructive hover:bg-destructive/10"
                disabled={saving || isBusy('delete-check') || isBusy('delete')}
                onClick={() => runAction('delete-check', handleDeleteScheduleClick)}
              >
                {isBusy('delete-check') ? (
                  <>
                    <LoaderSpinner size="sm" className="mr-2" />
                    Checking…
                  </>
                ) : (
                  'Delete schedule'
                )}
              </Button>
            )}
            {selectedPeriod.isNew && onCancelNew && (
              <Button
                type="button"
                variant="outline"
                className="h-10 px-5"
                disabled={saving}
                onClick={onCancelNew}
              >
                Cancel
              </Button>
            )}
            {!isPmPendingApprovalPeriod && (
              <Button
                type="button"
                className="h-10 w-[220px] justify-center bg-primary px-5 font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
                disabled={saving || !projectId || !hasChanges || !!overlapWarning || !!sessionDateConflict}
                onClick={handleSave}
              >
                {saving ? 'Saving…' : isProjectManager ? 'Save and Approve' : 'Save'}
              </Button>
            )}
            {isPmPendingApprovalPeriod && (
              <Button
                type="button"
                variant="outline"
                className="h-10 w-[220px] justify-center border-emerald-700 bg-white px-5 font-semibold text-emerald-700 shadow-sm hover:bg-emerald-50"
                disabled={saving || isBusy('approve') || !projectId || (hasChanges && (!!overlapWarning || !!sessionDateConflict))}
                onClick={
                  hasChanges ? handleSave : () => runAction('approve', handleApprovePeriod)
                }
              >
                {saving || isBusy('approve') ? (
                  <>
                    <LoaderSpinner size="sm" className="mr-2" />
                    {hasChanges ? 'Saving…' : 'Approving…'}
                  </>
                ) : (
                  <>
                    <CircleCheck className="mr-2 h-[18px] w-[18px]" />
                    {pmPrimaryLabel}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
  );

  return (
    <Card
      className={cn(
        BOOKINGS_FONT,
        'mx-auto w-full max-w-none overflow-hidden border-border shadow-md lg:max-w-[min(100%,88rem)] 2xl:max-w-[min(100%,112rem)]',
      )}
    >
      <CardContent className="p-0">
        <Collapsible
          open={hideHeader ? true : scheduleOpen}
          onOpenChange={hideHeader ? undefined : setScheduleOpen}
        >
          {/* Always visible: expand/collapse, identity, optional status badges, wide timezone */}
          {!hideHeader && renderModeratorCardHeader()}

          <CollapsibleContent className="overflow-hidden">
            {topBanner && (
              <div className="border-b border-border bg-muted/20 px-4 py-3 sm:px-6">
                {topBanner}
              </div>
            )}
            <div
              className={cn(
                'border-b border-border bg-card px-4 py-4 sm:px-6',
                !canEdit && 'pointer-events-none opacity-60',
              )}
            >
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div className="flex flex-wrap items-end gap-5">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Start
              <span className="ml-1 text-destructive">*</span>
            </span>
            <DateInput
              value={rangeStart}
              onChange={(e) => {
                const next = e.target.value;
                const clamped = next && next < minSelectableDate ? minSelectableDate : next;
                setRangeStart(clamped);
                if (rangeEnd && clamped && rangeEnd < clamped) {
                  setRangeEnd(clamped);
                }
                if (dateErrors.start && next.trim()) {
                  setDateErrors((prev) => ({ ...prev, start: false }));
                }
              }}
              min={minSelectableDate}
              disabled={!canEdit}
              className={cn(
                'h-10 w-[160px] rounded-md border-border shadow-sm sm:w-[180px]',
                (!rangeStart || dateErrors.start) &&
                  'border-destructive focus-visible:ring-destructive/30',
                !rangeStart && 'text-muted-foreground',
              )}
              title="Start"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              End (inclusive)
              <span className="ml-1 text-destructive">*</span>
            </span>
            <DateInput
              value={rangeEnd}
              onChange={(e) => {
                const next = e.target.value;
                setRangeEnd(next);
                if (dateErrors.end && next.trim()) {
                  setDateErrors((prev) => ({ ...prev, end: false }));
                }
              }}
              min={
                rangeStart && rangeStart > minSelectableDate
                  ? rangeStart
                  : minSelectableDate
              }
              disabled={!canEdit}
              className={cn(
                'h-10 w-[160px] rounded-md border-border shadow-sm sm:w-[180px]',
                (!rangeEnd || dateErrors.end) &&
                  'border-destructive focus-visible:ring-destructive/30',
                !rangeEnd && 'text-muted-foreground',
              )}
              title="End"
            />
          </div>
          </div>
          {sessionDateConflict && (
            <div className="basis-full text-xs text-destructive">
              {sessionDateConflict}
            </div>
          )}
          {isProjectManager && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {canSelectApprove && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex h-9 items-center">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(c) => onToggleGroupSelection(groupKey, Boolean(c))}
                          id={approveControlId}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Include in bulk approve</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
            </div>
            </div>

            <div className="border-b border-border bg-card px-4 pb-5 pt-2 sm:px-6">
        <p className="mb-3 text-sm font-medium text-foreground">Weekly hours</p>
        <div className="overflow-hidden rounded-lg border border-border bg-background shadow-sm">
        {WEEKDAY_LABELS.map((label, dow) => {
          const slots = week[dow];
          const ghosts = deletedGhostsByDay[dow] ?? [];
          return (
            <div
              key={dow}
              className="flex min-w-0 flex-col border-b border-border last:border-b-0 sm:flex-row sm:items-stretch"
            >
              <div className="flex shrink-0 items-center border-b border-border bg-muted/30 px-4 py-2.5 text-sm font-semibold text-foreground sm:w-[min(8rem,28%)] sm:border-b-0 sm:border-r sm:py-3">
                {label}
              </div>
              <div className="flex min-w-0 flex-1 flex-col bg-background">
                {slots.length === 0 && ghosts.length === 0 ? (
                  <div className="flex min-h-[42px] min-w-0 flex-1 flex-col items-stretch sm:flex-row">
                    <div className="flex min-h-[40px] flex-1 items-center justify-center px-4 py-2.5 sm:border-r sm:py-3">
                      <div className="flex min-h-[40px] w-full max-w-full items-center justify-center rounded-md border border-dashed border-muted-foreground/35 bg-muted/15 px-3">
                        <span className="text-sm text-muted-foreground">Not bookable</span>
                      </div>
                    </div>
                    <div className="flex w-full shrink-0 items-center justify-center gap-1 border-t border-border bg-muted/20 px-2 py-2 sm:w-[6.75rem] sm:border-l sm:border-t-0 sm:py-3">
                      {canEdit && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className={BOOKINGS_ICON_BTN}
                          onClick={() => addSlot(dow)}
                          aria-label={`Add hours on ${label}`}
                        >
                          <Plus className="h-4 w-4" strokeWidth={2} />
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                  {slots.map((slot, idx) => {
                    const kind = editableSlotKind(dow, slot);
                    return (
                    <div
                      key={`${dow}-${idx}`}
                      className={cn(
                        'flex min-w-0 flex-1 flex-col sm:flex-row sm:items-stretch',
                        idx > 0 && 'border-t border-border',
                        kind !== 'unchanged' && CHANGE_STYLES[kind],
                      )}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2 px-4 py-2.5 sm:border-r sm:py-3">
                        <div className="grid min-w-0 flex-1 grid-cols-2 gap-2">
                          <Select
                            value={slot.start}
                            onValueChange={(v) => updateSlot(dow, idx, 'start', v)}
                            disabled={!canEdit}
                          >
                            <SelectTrigger className={TIME_SELECT_TRIGGER}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {START_TIME_OPTIONS.map((t) => (
                                <SelectItem key={t} value={t}>
                                  {formatHmForDisplay(t)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={slot.end}
                            onValueChange={(v) => updateSlot(dow, idx, 'end', v)}
                            disabled={!canEdit}
                          >
                            <SelectTrigger className={TIME_SELECT_TRIGGER}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {END_TIME_OPTIONS.map((t) => (
                                <SelectItem key={t} value={t}>
                                  {formatHmForDisplay(t)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {kind !== 'unchanged' && (
                          <Badge
                            variant="outline"
                            className={cn(
                              'shrink-0 text-xs font-medium',
                              CHANGE_BADGE_STYLES[kind],
                            )}
                          >
                            {CHANGE_LABEL[kind]}
                          </Badge>
                        )}
                      </div>
                      <div className="flex w-full shrink-0 items-center justify-center gap-1 border-t border-border bg-muted/20 px-1.5 py-2 sm:w-[6.75rem] sm:border-l sm:border-t-0 sm:py-3">
                        {canEdit && (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className={BOOKINGS_ICON_BTN}
                              onClick={() => removeSlot(dow, idx)}
                              aria-label="Remove interval"
                            >
                              <X className="h-4 w-4" strokeWidth={2} />
                            </Button>
                            {idx === slots.length - 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className={BOOKINGS_ICON_BTN}
                                onClick={() => addSlot(dow)}
                                aria-label="Add interval this day"
                              >
                                <Plus className="h-4 w-4" strokeWidth={2} />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    );
                  })}
                  {ghosts.map((ghost, gi) => (
                    <div
                      key={`ghost-${dow}-${gi}`}
                      className={cn(
                        'flex min-w-0 flex-1 flex-col sm:flex-row sm:items-stretch',
                        (slots.length > 0 || gi > 0) && 'border-t border-border',
                        CHANGE_STYLES.deleted,
                      )}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2 px-4 py-2.5 sm:border-r sm:py-3">
                        <span className="text-sm">
                          {formatHmForDisplay(ghost.start)} – {formatHmForDisplay(ghost.end)}
                        </span>
                        <Badge
                          variant="outline"
                          className={cn('shrink-0 text-xs font-medium', CHANGE_BADGE_STYLES.deleted)}
                        >
                          {CHANGE_LABEL.deleted}
                        </Badge>
                      </div>
                      <div className="flex w-full shrink-0 items-center justify-center gap-1 border-t border-border bg-muted/20 px-1.5 py-2 sm:w-[6.75rem] sm:border-l sm:border-t-0 sm:py-3">
                        {canEdit && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={BOOKINGS_ICON_BTN}
                            onClick={() => restoreSlot(dow, ghost)}
                            aria-label="Restore removed interval"
                            title="Restore this slot"
                          >
                            <Undo2 className="h-4 w-4" strokeWidth={2} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {slots.length === 0 && canEdit && (
                    <div className="flex w-full items-center justify-end border-t border-border bg-muted/10 px-2 py-2 sm:py-2.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={BOOKINGS_ICON_BTN}
                        onClick={() => addSlot(dow)}
                        aria-label={`Add hours on ${label}`}
                      >
                        <Plus className="h-4 w-4" strokeWidth={2} />
                      </Button>
                    </div>
                  )}
                  </>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {overlapWarning && (
        <div
          className="flex items-start gap-2.5 border-b border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-950 sm:px-6"
          role="status"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-800" aria-hidden />
          <span>{overlapWarning}</span>
        </div>
      )}

      {canEdit && renderModeratorCardActions()}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove all working hours for this moderator on this project. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving || isBusy('delete')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={saving || isBusy('delete')}
              onClick={async (e) => {
                e.preventDefault();
                await runAction('delete', handleDeletePeriod);
                setShowDeleteConfirm(false);
              }}
            >
              {isBusy('delete') ? (
                <>
                  <LoaderSpinner size="sm" className="mr-2" />
                  Deleting…
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
